import { useState, useCallback } from "react"
import { useParams } from "react-router-dom"
import {
    NETWORKS,
    DEFAULT_NETWORK,
    NETWORK_ECHO_STORAGE_KEY,
    NETWORK_PREF_STORAGE_KEY,
    storedNetworkKey,
} from "../lib/config"
import { completeQuest, getQuestWalletAddress } from "../lib/quests"
import { trackNetworkVisit } from "../lib/questVerifier"

/**
 * Hook for managing the active Gno network.
 * Reads the network key from the /:network URL param (primary) or localStorage (fallback).
 * On switch, navigates to the new network URL and reloads to re-initialize RPC config.
 */
export function useNetwork() {
    const { network: urlNetwork } = useParams<{ network: string }>()
    const resolvedKey = (urlNetwork && NETWORKS[urlNetwork]) ? urlNetwork : storedNetworkKey()
    const [networkKey] = useState(resolvedKey)

    const network = NETWORKS[networkKey] || NETWORKS[DEFAULT_NETWORK]

    const switchNetwork = useCallback((key: string) => {
        if (!NETWORKS[key]) return
        // "Switching" to the network you are already on is not a switch: it would
        // award the quest and trigger a full page load to the same URL. This guard
        // used to live at the CALL SITES — five of them, each enforcing it a
        // different way (an explicit check in Settings, "a <select> can't fire
        // onChange for its current value", "the banner only renders on a
        // mismatch", …). That is the drifting-copies shape this PR exists to
        // remove, and the copies already disagree: FeedComposer gates on
        // ACTIVE_NETWORK_KEY (module-load, storage-derived) while this compares
        // networkKey (URL-derived), so before NetworkSync reloads they can differ.
        if (key === networkKey) return
        // Quest instrumentation lives HERE, not at the call sites. It used to sit
        // in TopBar's onChange alone, so the other three switch surfaces
        // (MobileTabBar, Settings, ChainMismatchBanner) silently dropped credit for
        // the "Network Hopper" quest — and Settings only stopped being harmless
        // because its button could not actually switch. One home, like the network
        // resolution rule itself. Both writes are synchronous localStorage, so they
        // land before the navigation below.
        completeQuest("switch-network")
        const questAddr = getQuestWalletAddress()
        if (questAddr) trackNetworkVisit(questAddr, key)
        // Recorded as a PREFERENCE, apart from the URL echo NetworkSync rewrites on
        // every visit (see resolveNetworkKey). The echo is kept in step for the
        // readers that still treat it as "the network the user is on".
        localStorage.setItem(NETWORK_PREF_STORAGE_KEY, key)
        localStorage.setItem(NETWORK_ECHO_STORAGE_KEY, key)
        // Navigate to the same path but with the new network prefix
        const currentPath = window.location.pathname
        // Strip current network prefix if present
        const segments = currentPath.split("/").filter(Boolean)
        const firstSegment = segments[0]
        const restPath = (firstSegment && NETWORKS[firstSegment])
            ? "/" + segments.slice(1).join("/")
            : currentPath
        window.location.href = `/${key}${restPath || "/dashboard"}`
    }, [networkKey])

    return {
        networkKey,
        chainId: network.chainId,
        rpcUrl: network.rpcUrl,
        label: network.label,
        switchNetwork,
        networks: NETWORKS,
    }
}
